<template>
  <div class="container-fluid d-flex h-100 flex-column">
    <div class="row">
      <div class="col">
        <div class="row g-0 pb-0 current align-self-center">
          <div class="col-5 col-md-3 align-self-center">
            <h5 style="margin-bottom: 0px !important">{{ t("billing_summary.current_plan") }}</h5>
          </div>
          <div class="col pe-0 my-2 text-end" v-if="billingDetails.cancellationDate">
            <span
              class="d-inline-block"
              data-bs-toggle="tooltip"
              data-bs-placement="left"
              title="Not implemented. Your current plan is scheduled to be cancelled on the provided date. If you wish to resume the plan please content Authentic Labs support"
            >
              <button class="btn btn-outline-primary btn-sm rounded-pill" disabled>Resume plan</button>
            </span>
          </div>
          <div class="col pe-0 my-2 text-end" v-else>
            <div class="btn-group" role="group">
              <button class="btn btn-outline-secondary btn-sm rounded-pill" type="button" @click="cancelPlan">
                {{ billingDetails.paymentInterval === "yearly" ? t("billing_summary.cancel_at_renewal") : t("billing_summary.cancel_plan") }}
              </button>
              <button class="btn btn-primary btn-sm rounded-pill ms-2" type="button" @click="updatePlan">{{ t("billing_summary.update_plan") }}</button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row g-0" v-if="billingDetails.pendingChange">
      <p class="card-text text-muted mb-0">
        {{
          t("billing_summary.pending_change", {
            plan: planLabels[billingDetails.pendingChange.plan],
            interval: billingDetails.pendingChange.interval,
            date: formatLongDate(billingDetails.pendingChange.effectiveDate),
          })
        }}
        <button type="button" class="btn btn-link btn-sm p-0 ms-1 align-baseline" @click="cancelPendingChange">
          {{ t("billing_summary.cancel_scheduled_change") }}
        </button>
      </p>
    </div>

    <div class="row pt-2">
      <div class="col">
        <div class="row g-0">
          <p class="card-text subscription-plan">{{ billingDetails.subscriptionName === "Pro" ? "Professional" : billingDetails.subscriptionName }}</p>
        </div>
        <div class="row pb-0 g-0">
          <p class="card-text subscription-price">
            {{ $filters.formatCurrency(billingDetails.planPrice || 0) }} / {{ t(`billing_summary.${billingDetails.paymentInterval}`) }}
          </p>
        </div>
        <div class="row g-0" v-if="billingDetails.cancellationDate">
          <p class="card-text subscription-renewal">Your current plan terminates on {{ formatLongDate(billingDetails.cancellationDate) }}</p>
        </div>
        <div class="row g-0" v-else>
          <p class="card-text subscription-renewal">{{ t("billing_summary.your_plan_renews_on") }} {{ formatLongDate(billingDetails.renewalDate) }}</p>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col">
        <div class="row g-0 pt-3 pb-0 current align-self-center">
          <div class="col-5 col-md-3 align-self-center">
            <h5 style="margin-bottom: 0px !important">{{ t("billing_summary.add_ons") }}</h5>
          </div>
          <div class="col pe-0 my-2 text-end">
            <button class="btn btn-outline-primary btn-sm rounded-pill" @click="manageAddOns">{{ t("billing_summary.manage_add_ons") }}</button>
          </div>
        </div>
      </div>
    </div>

    <div class="row g-0">
      <div class="col">
        <p v-if="!billingDetails.addOns?.length" class="card-text text-muted pt-2">{{ t("billing_summary.no_add_ons") }}</p>
        <table v-else class="table table-borderless table-sm w-auto">
          <tbody>
            <tr v-for="(addOn, index) in billingDetails.addOns" :key="index">
              <td width="350">
                {{ addOnLabel(addOn) }} <span v-if="pendingRemovalText(addOn)" class="text-muted small ms-1">({{ pendingRemovalText(addOn) }})</span>
              </td>
              <td>
                {{ $filters.formatCurrency(addOnAmount(addOn)) }}
                {{
                  addOn.type === "additional_codes"
                    ? t("billing_summary.one_time")
                    : "/ " + (isYearly ? t("billing_add_ons.period_year") : t("billing_add_ons.period_month"))
                }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="row">
      <div class="col">
        <div class="row g-0 pt-3 pb-0 current align-self-center" no-gutters>
          <div class="col-5 col-md-3 align-self-center">
            <h5 style="margin-bottom: 0px !important">{{ t("billing_summary.payment_method") }}</h5>
          </div>
          <div class="col pe-0 my-2 text-end">
            <button class="btn btn-outline-primary btn-sm rounded-pill" @click="addPayment">{{ t("billing_summary.add_payment_method") }}</button>
          </div>
        </div>
      </div>
    </div>

    <div id="overlay" :style="showOverlay ? 'display:flex' : 'display:none'" class="analytics-db-container">
      <div id="text" class="d-flex flex-column align-items-center justify-content-center">
        <div class="row">
          <div class="spinner-border text-primary" role="status"></div>
        </div>
        <div class="row">
          <span>{{ paymentMethodMessage }}</span>
        </div>
      </div>
    </div>

    <div class="row g-0 payment-methods">
      <div class="col">
        <table id="table" class="table table-borderless table-sm w-auto">
          <tbody>
            <tr v-for="(card, index) in billingDetails.paymentMethods" :key="index">
              <td width="350">
                <span class="pe-1">{{ $filters.capitalizeFirst(card.brand) }} </span>
                <span class="pe-1"><span>&bull;&bull;&bull;&bull;</span> {{ card.last_four }}</span>
                <span v-if="card.default" class="default">{{ t("billing_summary.default") }}</span>
              </td>
              <td width="135">
                <span class="pe-1">{{ t("billing_summary.expires") }} {{ $filters.formatMonth(card.exp_month as number) }}/{{ card.exp_year }}</span>
              </td>
              <td style="z-index: 0" v-if="!card.default">
                <div class="btn-group">
                  <button
                    type="button"
                    class="btn action-button py-0 px-1"
                    @click.stop="makeDefault(index, card.id as string)"
                    data-bs-toggle="tooltip"
                    :title="t('billing_summary.make_default_payment_method_tooltip')"
                  >
                    <i class="bi bi-check2-circle" style="font-size: 1em"></i>
                  </button>
                  <button
                    type="button"
                    class="btn action-button py-0 px-1"
                    @click.stop="deletePaymentMethod(index, card.id as string)"
                    data-bs-toggle="tooltip"
                    :title="t('billing_summary.delete_payment_method_tooltip')"
                  >
                    <i class="bi bi-trash3" style="opacity: 1; font-size: 1em; color: #e3342f"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="row pt-3" no-gutters>
      <div class="col">
        <h5 class="current">{{ t("billing_summary.billing_history") }}</h5>
      </div>
    </div>

    <div class="row" w>
      <div class="col history-container">
        <p v-if="!billingDetails.billingHistory?.length" class="card-text text-muted pt-2">{{ t("billing_summary.no_invoices") }}</p>
        <div class="card border-0 py-2" v-for="(billingRecord, index) in billingDetails.billingHistory" :key="index">
          <div class="row">
            <div class="col-4 col-md-3">
              <p class="card-text">{{ billingRecord.date }}</p>
            </div>
            <div class="col-3 col-md-3 col-lg-2">
              <p class="card-text">{{ billingRecord.amount }}</p>
            </div>
            <div class="col overflow-ellipses">
              <p class="overflow-ellipses card-text" data-bs-toggle="tooltip" data-bs-placement="top" data-bs-variant :title="billingRecord.description">
                {{ billingRecord.description }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { getCustomerStatus, setDefaultPayment, removePayment, cancelScheduledChange } from "../../services/PaymentService";
  import User from "../../models/User";
  import Billing from "../../models/Billing";
  import { computed, onBeforeUnmount, ref, inject, onMounted, nextTick, onUnmounted } from "vue";
  import { useStore } from "vuex";
  import { Tooltip } from "bootstrap";
  import { useI18n } from "vue-i18n";
  import { useToast } from "vue-toastification";

  const $store = useStore();
  const toast = useToast();
  const emitter: any = inject("emitter");
  const tooltipInstances = ref<InstanceType<typeof Tooltip>[]>([]);
  const { t } = useI18n();

  const props = defineProps<{
    billingDetails: Billing;
  }>();

  const user = computed((): User => {
    return $store.getters.authUser;
  });

  const show = ref(true);
  const showOverlay = ref(false);
  const showMainOverlay = ref(false);
  const paymentMethodMessage = ref("");
  type AddOn = NonNullable<Billing["addOns"]>[number];

  const modalTitle = "Billing";
  const isYearly = computed((): boolean => props.billingDetails.paymentInterval === "yearly");

  onBeforeUnmount(() => {
    emitter.off("billing-management");
  });

  onMounted(async () => {
    // console.log(this.billingDetails)
    await nextTick();
    const tooltipElements = document.querySelectorAll('[data-bs-toggle="tooltip"]');
    tooltipElements.forEach((element) => {
      const tooltip = new Tooltip(element as HTMLElement, { trigger: "hover" });
      tooltipInstances.value.push(tooltip);
    });
  });

  function updatePlan() {
    emitter.emit("update-plan");
  }

  function cancelPlan() {
    emitter.emit("cancel-plan");
  }

  function addPayment() {
    emitter.emit("billing-add-payment");
  }
  const planLabels: Record<string, string> = {
    basic: "Basic",
    pro: "Professional",
    enterprise: "Enterprise",
  };

  async function cancelPendingChange() {
    const response = await cancelScheduledChange();
    if (response.status === "success") {
      emitter.emit("billing-summary", "refresh");
    } else {
      toast.error(t("billing_summary.cancel_scheduled_change_failed"));
    }
  }

  function pendingRemovalText(addOn: AddOn) {
    const scheduled = props.billingDetails.scheduledAddOns;
    if (!scheduled || addOn.type === "additional_codes") return "";

    const date = formatLongDate(props.billingDetails.renewalDate);
    const next = scheduled.find((s) => s.type === addOn.type);
    if (!next) {
      return t("billing_summary.ends_on", { date });
    }
    if (next.quantity < (addOn.quantity ?? 1)) {
      return t("billing_summary.drops_to_on", { count: next.quantity, date });
    }
    return "";
  }

  function formatLongDate(value?: string | null) {
    if (!value) return "";
    const date = new Date(`${value}T00:00:00`);
    return date.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  }

  function addOnLabel(addOn: AddOn) {
    if (addOn.type === "additional_codes") {
      return t("billing_summary.add_on_types.additional_codes", { count: ((addOn.quantity ?? 0) * 1000).toLocaleString() });
    }
    if (addOn.type === "localization") {
      return t("billing_summary.add_on_types.localization_count", addOn.quantity ?? 1);
    }
    return t(`billing_summary.add_on_types.${addOn.type}`);
  }

  function addOnAmount(addOn: AddOn) {
    if (addOn.type === "additional_codes") {
      return (addOn.quantity ?? 0) * (addOn.unitPrice ?? 0);
    }
    return (addOn.quantity ?? 1) * (addOn.monthlyPrice ?? 0) * (isYearly.value ? 12 : 1);
  }

  function manageAddOns() {
    emitter.emit("billing-update-quantity", { productList: false, fromArchive: false });
  }

  async function makeDefault(idx: number, paymentMethod: string) {
    paymentMethodMessage.value = "Updating payment information...";
    showOverlay.value = true;
    const response = await setDefaultPayment({ paymentMethod });
    if (response?.status === "success") {
      props.billingDetails!.paymentMethods = response?.paymentMethods;
    }
    showOverlay.value = false;
  }

  async function deletePaymentMethod(idx: number, paymentMethod: string) {
    paymentMethodMessage.value = "Deleting selected payment method...";
    showOverlay.value = true;
    const response = await removePayment({ paymentMethod });
    if (response?.status === "success") {
      props.billingDetails!.paymentMethods = response?.paymentMethods;
    }
    showOverlay.value = false;
  }

  onBeforeUnmount(() => {
    tooltipInstances.value.forEach((tooltip) => tooltip.dispose());
    tooltipInstances.value = [];
  });
</script>

<style scoped>
  :deep .tooltip-inner {
    text-align: left;
  }

  #overlay {
    position: absolute;
    display: none;
    width: 98%;
    height: 98%;
    top: 0px;
    left: 8px;
    right: 0;
    bottom: 0;
    background-color: rgba(250, 250, 250, 0.85);
    z-index: 3;
    border-radius: 8px;
    align-items: center;
    justify-content: center;
  }

  .action-button,
  .action-button:focus .action-button:active {
    border: none !important;
    box-shadow: none !important;
    outline: none !important;
  }

  .payment-methods {
    max-height: 70px;
    overflow-y: auto;
  }

  .history-container {
    overflow: auto;
    max-height: 100px;
  }

  /* .history-container {
    overflow: auto;
    max-height: 136px;
  } */

  .billing-buttons {
    position: absolute;
    right: 0;
  }

  .overflow-ellipses {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .default-action {
    opacity: 0.5;
  }

  :deep .set-card-spacing {
    padding-right: 3rem !important;
  }

  :deep .set-expire-spacing {
    padding-right: 1.5rem !important;
  }

  :deep .b-table-sticky-header {
    max-height: 125px;
  }

  :deep .dropdown .btn {
    background-color: transparent !important;
    color: #495057 !important;
    border: none;
  }

  :deep .table th,
  .table td {
    vertical-align: middle !important;
    border-top: none;
    background-color: #fff !important;
  }

  h5 {
    font-family: "proximanova-medium", sans-serif !important;
  }

  .current {
    border-bottom: 1px solid lightgray;
    padding-bottom: 1rem;
  }

  .subscription-plan {
    font-family: "proximanova-bold", sans-serif;
    font-size: 20px;
  }

  .subscription-price {
    font-family: "proximanova-regular", sans-serif;
    font-size: 20px;
  }

  .subscription-renewal {
    margin-top: -0.5rem;
    font-family: "proximanova-regular", sans-serif;
    font-size: 16px;
    white-space: nowrap;
  }

  .default {
    background-color: #e9ecef;
    border-radius: 0.25rem;
    padding-left: 5px;
    padding-right: 5px;
    font-size: 14px;
  }
</style>
